import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, m } from "framer-motion";
import {
  Activity,
  ArrowRight,
  BarChart3,
  Check,
  CircleDot,
  Database,
  Gauge,
  LockKeyhole,
  RadioTower,
  ShieldCheck,
  Sparkles,
  UserCheck,
  Users,
  Wrench,
} from "lucide-react";

const UI_EASE = [0.23, 1, 0.32, 1];
const AI_PURPLE = "#7C3AED";

const KIND_ICON = {
  action: Activity,
  evidence: Database,
  provider: RadioTower,
  record: CircleDot,
  review: UserCheck,
  scope: LockKeyhole,
};

function Kicker({ icon: Icon, children, dark = false }) {
  return (
    <p
      className={
        "inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] " +
        (dark ? "text-white/55" : "text-ink-subtle")
      }
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {children}
    </p>
  );
}

function InteractiveWorkflow({ mod, content, accent, reduce }) {
  const [active, setActive] = useState(0);
  const [keyboardSelection, setKeyboardSelection] = useState(false);
  const tabs = useRef([]);
  const stages = content.visual.stages;
  const panelId = `deep-dive-${mod.key}-stage-panel`;

  useEffect(() => {
    setActive(0);
    setKeyboardSelection(false);
  }, [mod.key]);

  const selectWithKeyboard = (event, index) => {
    const last = stages.length - 1;
    let next = null;

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      next = index === last ? 0 : index + 1;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      next = index === 0 ? last : index - 1;
    } else if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = last;
    }

    if (next === null) return;
    event.preventDefault();
    setKeyboardSelection(true);
    setActive(next);
    tabs.current[next]?.focus();
  };

  const current = stages[active];
  const CurrentIcon = KIND_ICON[current.kind] || CircleDot;
  const instant = reduce || keyboardSelection;
  const activeScale = (active + 1) / stages.length;

  return (
    <div
      className="mt-8 overflow-hidden rounded-[28px] border border-white/10 bg-[var(--vp-hero-navy)] text-white shadow-[0_24px_70px_-44px_rgba(3,10,24,0.9)]"
      data-testid="deep-dive-workflow"
    >
      <div className="flex flex-col gap-4 border-b border-white/10 px-5 py-5 sm:flex-row sm:items-end sm:justify-between sm:px-7">
        <div className="max-w-2xl">
          <Kicker icon={Activity} dark>Interactive operating model</Kicker>
          <h3 className="mt-2 font-heading text-xl font-bold tracking-tight text-white sm:text-[24px]">
            {content.visual.title}
          </h3>
          <p className="mt-2 text-[13.5px] leading-relaxed text-white/65">
            {content.visual.description}
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 text-[11px] font-semibold text-white/65">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          Illustrative workflow · no customer data
        </span>
      </div>

      <div className="overflow-x-auto px-5 pt-6 sm:px-7">
        <div
          className="relative pb-1"
          style={{ minWidth: `${Math.max(640, stages.length * 158)}px` }}
        >
          <div className="absolute left-5 right-5 top-[22px] h-px bg-white/10" aria-hidden="true">
            <m.span
              className="block h-full origin-left"
              initial={false}
              animate={{ transform: `scaleX(${activeScale})` }}
              transition={{ duration: instant ? 0 : 0.18, ease: UI_EASE }}
              style={{ backgroundColor: accent }}
            />
          </div>

          <div
            role="tablist"
            aria-label={content.visual.title}
            className="relative grid gap-2"
            style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(148px, 1fr))` }}
          >
            {stages.map((item, index) => {
              const Icon = KIND_ICON[item.kind] || CircleDot;
              const selected = index === active;
              return (
                <button
                  key={item.label}
                  ref={(node) => { tabs.current[index] = node; }}
                  id={`deep-dive-${mod.key}-stage-${index}`}
                  type="button"
                  role="tab"
                  aria-controls={panelId}
                  aria-selected={selected}
                  tabIndex={selected ? 0 : -1}
                  onKeyDown={(event) => selectWithKeyboard(event, index)}
                  onClick={() => {
                    setKeyboardSelection(false);
                    setActive(index);
                  }}
                  className={
                    "min-h-16 rounded-2xl border px-3 py-3 text-left transition-[background-color,border-color,color,box-shadow] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--vp-hero-navy)] " +
                    (selected
                      ? "border-white/40 bg-[var(--vp-scene-paper)] text-[var(--vp-hero-navy)] shadow-lg"
                      : "border-white/10 bg-[var(--vp-scene-panel)] text-white/70 hover:border-white/25 hover:text-white")
                  }
                  data-testid={`deep-dive-stage-${index}`}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className="grid h-7 w-7 shrink-0 place-items-center rounded-full border"
                      style={{
                        borderColor: selected ? `${accent}55` : "rgba(255,255,255,0.16)",
                        backgroundColor: selected ? `${accent}12` : "rgba(255,255,255,0.04)",
                        color: selected ? accent : "rgba(255,255,255,0.78)",
                      }}
                      aria-hidden="true"
                    >
                      <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-[0.13em] ${selected ? "text-slate-600" : "text-white/70"}`}>
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </span>
                  <span className="mt-2 block text-[12.5px] font-semibold leading-snug">
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="px-5 pb-6 pt-5 sm:px-7 sm:pb-7">
        <AnimatePresence mode="wait" initial={false}>
          <m.div
            key={`${mod.key}-${active}`}
            id={panelId}
            role="tabpanel"
            aria-labelledby={`deep-dive-${mod.key}-stage-${active}`}
            tabIndex={0}
            initial={instant ? false : { opacity: 0, transform: "translateY(5px)" }}
            animate={{ opacity: 1, transform: "translateY(0px)" }}
            exit={instant ? undefined : { opacity: 0, transform: "translateY(-3px)" }}
            transition={{ duration: instant ? 0 : 0.18, ease: UI_EASE }}
            className="grid gap-5 rounded-2xl border border-white/10 bg-white/[0.05] p-5 outline-none focus-visible:ring-2 focus-visible:ring-white/80 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)]"
            data-testid="deep-dive-stage-panel"
          >
            <div>
              <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white/60">
                <CurrentIcon className="h-3.5 w-3.5" aria-hidden="true" />
                Stage {active + 1} of {stages.length}
              </p>
              <p className="mt-2 font-heading text-[17px] font-semibold leading-snug text-white">
                {current.label}
              </p>
              <p className="mt-2 text-[13.5px] leading-relaxed text-white/70">{current.detail}</p>
            </div>
            <div className="border-t border-white/10 pt-4 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
              <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white/60">
                <UserCheck className="h-3.5 w-3.5" aria-hidden="true" /> Control point
              </p>
              <p className="mt-2 text-[13px] leading-relaxed text-white/70">{current.control}</p>
            </div>
            <div className="border-t border-white/10 pt-4 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
              <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white/60">
                <Database className="h-3.5 w-3.5" aria-hidden="true" /> Evidence retained
              </p>
              <p className="mt-2 text-[13px] leading-relaxed text-white/70">{current.evidence}</p>
            </div>
          </m.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function WorkflowList({ content, accent }) {
  return (
    <section aria-labelledby="deep-dive-capabilities-h">
      <Kicker icon={CircleDot}>Core workflows and features</Kicker>
      <h3
        id="deep-dive-capabilities-h"
        className="mt-2 max-w-2xl font-heading text-[22px] font-bold tracking-tight text-ink sm:text-[25px]"
      >
        The operational work behind the promise
      </h3>
      <ol className="mt-5 border-t border-line">
        {content.workflows.map((item, index) => (
          <li
            key={item.title}
            className="grid gap-2 border-b border-line py-4 sm:grid-cols-[42px_minmax(160px,0.72fr)_minmax(0,1.28fr)] sm:gap-4"
          >
            <span
              className="grid h-7 w-7 place-items-center rounded-full text-[10px] font-bold tabular-nums text-ink-2"
              style={{ backgroundColor: `${accent}12` }}
              aria-hidden="true"
            >
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="text-[13.5px] font-semibold leading-relaxed text-ink">{item.title}</span>
            <span className="text-[13.5px] leading-relaxed text-ink-2">{item.description}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function AutomationPanel({ mod, content }) {
  const usesAira = mod.aiFeature || /aira/i.test(content.automation.label);
  const PanelIcon = usesAira ? Sparkles : ShieldCheck;
  const highlight = usesAira ? AI_PURPLE : mod.groupColor;

  return (
    <section
      className="relative overflow-hidden rounded-[26px] border border-white/10 bg-[var(--vp-hero-navy)] p-6 text-white sm:p-7"
      aria-labelledby="deep-dive-automation-h"
    >
      <div
        className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full blur-3xl"
        style={{ backgroundColor: `${highlight}1f` }}
        aria-hidden="true"
      />
      <div className="relative">
        <Kicker icon={PanelIcon} dark>{content.automation.label}</Kicker>
        <h3
          id="deep-dive-automation-h"
          className="mt-3 max-w-xl font-heading text-[22px] font-bold leading-tight tracking-tight text-white sm:text-[25px]"
        >
          {content.automation.title}
        </h3>
        <p className="mt-3 text-[13.5px] leading-relaxed text-white/70">{content.automation.summary}</p>

        <ul className="mt-6 divide-y divide-white/10 border-y border-white/10">
          {content.automation.actions.map((action) => (
            <li key={action} className="flex items-start gap-3 py-3.5">
              <span
                className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full"
                style={{ backgroundColor: `${highlight}22`, color: highlight }}
              >
                <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
              </span>
              <span className="text-[13px] leading-relaxed text-white/70">{action}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 rounded-2xl border border-orange/25 bg-orange/[0.08] p-4">
          <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-orangeWarm-300">
            <UserCheck className="h-3.5 w-3.5" aria-hidden="true" /> Human boundary
          </p>
          <p className="mt-2 text-[13px] leading-relaxed text-white/80">{content.automation.boundary}</p>
        </div>
      </div>
    </section>
  );
}

function DailyUsePanel({ content }) {
  return (
    <section
      className="rounded-[26px] border border-line bg-surface p-6 sm:p-7"
      aria-labelledby="deep-dive-uses-h"
    >
      <Kicker icon={Activity}>Key daily uses</Kicker>
      <h3 id="deep-dive-uses-h" className="mt-3 font-heading text-[22px] font-bold tracking-tight text-ink sm:text-[25px]">
        What the school team does here
      </h3>
      <ol className="mt-5 space-y-1">
        {content.uses.map((item, index) => (
          <li key={item} className="grid grid-cols-[32px_minmax(0,1fr)] gap-3 border-b border-line py-3.5 last:border-0">
            <span className="pt-0.5 text-[11px] font-bold tabular-nums text-ink-subtle" aria-hidden="true">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="text-[13.5px] leading-relaxed text-ink-2">{item}</span>
          </li>
        ))}
      </ol>

      <div className="mt-6 border-t border-line pt-5">
        <Kicker icon={Users}>Used by</Kicker>
        <ul className="mt-3 flex flex-wrap gap-2">
          {content.roles.map((role) => (
            <li
              key={role}
              className="rounded-full border border-line bg-muted px-3 py-1.5 text-[12px] font-semibold text-ink-2"
            >
              {role}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-[11.5px] leading-relaxed text-ink-subtle">
          Actual access follows the school's plan, role permissions and record scope.
        </p>
      </div>
    </section>
  );
}

function Metrics({ content }) {
  return (
    <section className="mt-10" aria-labelledby="deep-dive-metrics-h">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Kicker icon={BarChart3}>Measurable operating indicators</Kicker>
          <h3 id="deep-dive-metrics-h" className="mt-2 font-heading text-[22px] font-bold tracking-tight text-ink sm:text-[25px]">
            Metrics to track — not guaranteed ROI
          </h3>
        </div>
        <p className="max-w-md text-[12.5px] leading-relaxed text-ink-subtle">
          Establish a baseline before rollout, agree the reporting period, then compare the school's own source evidence.
        </p>
      </div>

      <dl className="mt-5 grid border-y border-line lg:grid-cols-3">
        {content.metrics.map((item, index) => (
          <div
            key={item.label}
            className={
              "py-5 lg:px-6 " +
              (index > 0 ? "border-t border-line lg:border-l lg:border-t-0" : "lg:pl-0")
            }
          >
            <dt className="flex items-center gap-2 text-[13.5px] font-bold text-ink">
              <Gauge className="h-4 w-4 text-primary" aria-hidden="true" /> {item.label}
            </dt>
            <dd className="mt-3">
              <p className="font-mono text-[11.5px] leading-relaxed text-ink-2">{item.formula}</p>
              <p className="mt-2 text-[12.5px] leading-relaxed text-ink-subtle">{item.interpretation}</p>
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Availability({ content }) {
  return (
    <section
      className="mt-8 grid gap-5 rounded-[22px] border border-line bg-muted/45 p-5 sm:p-6 lg:grid-cols-[minmax(220px,0.7fr)_minmax(0,1.3fr)]"
      aria-labelledby="deep-dive-availability-h"
      data-testid="deep-dive-availability"
    >
      <div>
        <Kicker icon={Wrench}>Prerequisites and availability</Kicker>
        <h3 id="deep-dive-availability-h" className="mt-2 font-heading text-[19px] font-bold tracking-tight text-ink">
          Confirm the operating context before rollout.
        </h3>
        <p className="mt-3 inline-flex rounded-full border border-line bg-surface px-3 py-1.5 text-[11.5px] font-semibold leading-relaxed text-ink-2">
          {content.availability.status}
        </p>
      </div>
      <ul className="divide-y divide-line border-y border-line">
        {content.availability.prerequisites.map((item) => (
          <li key={item} className="flex items-start gap-3 py-3">
            <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <span className="text-[12.5px] leading-relaxed text-ink-2">{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function ModuleDeepDive({ mod, content, reduce = false }) {
  if (!mod || !content) return null;
  const accent = mod.aiFeature ? AI_PURPLE : mod.groupColor;

  return (
    <section
      className="border-y border-line bg-canvas"
      aria-labelledby={`deep-dive-${mod.key}-h`}
      data-testid="module-deep-dive"
      data-module-key={mod.key}
    >
      <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16 lg:py-20">
        <m.header
          initial={reduce ? false : { opacity: 0, transform: "translateY(12px)" }}
          whileInView={reduce ? undefined : { opacity: 1, transform: "translateY(0px)" }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: reduce ? 0 : 0.28, ease: UI_EASE }}
          className="grid gap-6 border-b border-line pb-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-end"
        >
          <div>
            <Kicker icon={ShieldCheck}>Flagship workflow deep dive</Kicker>
            <h2
              id={`deep-dive-${mod.key}-h`}
              className="mt-3 max-w-3xl font-heading text-[29px] font-bold leading-[1.08] tracking-tight text-ink [text-wrap:balance] sm:text-[36px]"
            >
              {content.title}
            </h2>
            <p className="mt-3 text-[15px] font-semibold leading-relaxed text-accent">{content.tagline}</p>
          </div>
          <p className="max-w-3xl text-[15px] leading-[1.75] text-ink-2">{content.pitch}</p>
        </m.header>

        <InteractiveWorkflow mod={mod} content={content} accent={accent} reduce={reduce} />

        <div className="mt-12">
          <WorkflowList content={content} accent={accent} />
        </div>

        <div className="mt-10 grid gap-5 lg:grid-cols-2 lg:items-start">
          <AutomationPanel mod={mod} content={content} />
          <DailyUsePanel content={content} />
        </div>

        <Metrics content={content} />
        <Availability content={content} />
      </div>
    </section>
  );
}
